import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Container, PageHeader, Spinner, EmptyState } from "@/components/common";
import { formatDateShort, formatTime, formatPrice } from "@/lib/format";
import { MapPin, CalendarDays, Ticket, Megaphone, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const CATS = [
  { key: "all", label: "All" },
  { key: "club", label: "Our events" },
  { key: "community", label: "Other socials" },
];

export function EventCategoryBadge({ category }) {
  const ours = category === "club";
  return (
    <span data-testid="event-category-badge"
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${ours ? "bg-[#C85A32] text-white" : "bg-[#EBE1D8] text-[#5C4A42]"}`}>
      {ours ? "Our event" : "Community"}
    </span>
  );
}

const EMPTY_SUB = { contact_name: "", email: "", club_name: "", event_title: "", event_date: "", venue: "", details: "" };

function SubmitEventDialog({ open, onOpenChange }) {
  const [f, setF] = useState(EMPTY_SUB);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = "w-full rounded-xl border border-border bg-white px-3 py-2.5 text-[#2D1E18] outline-none focus:border-[#C85A32]";
  const lbl = "mb-1 block text-sm font-semibold text-[#2D1E18]";
  const submit = async () => {
    if (!f.contact_name || !f.email || !f.event_title) { toast.error("Please add your name, email and the event title."); return; }
    setBusy(true);
    try {
      await api.post("/event-submissions", f);
      toast.success("Thanks! We've received your event and will review it soon.");
      setF(EMPTY_SUB); onOpenChange(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Something went wrong, please try again."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Add your event</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-[#5C4A42]">Running a line dance social nearby? Send us the details and we'll review it for our events page.</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Your name</label><input className={inp} value={f.contact_name} onChange={set("contact_name")} data-testid="submit-name" /></div>
            <div><label className={lbl}>Your email</label><input className={inp} type="email" value={f.email} onChange={set("email")} data-testid="submit-email" /></div>
          </div>
          <div><label className={lbl}>Club / group name</label><input className={inp} value={f.club_name} onChange={set("club_name")} data-testid="submit-club" /></div>
          <div><label className={lbl}>Event title</label><input className={inp} value={f.event_title} onChange={set("event_title")} data-testid="submit-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Date</label><input className={inp} type="date" value={f.event_date} onChange={set("event_date")} data-testid="submit-date" /></div>
            <div><label className={lbl}>Venue</label><input className={inp} value={f.venue} onChange={set("venue")} data-testid="submit-venue" /></div>
          </div>
          <div><label className={lbl}>Details</label><textarea className={inp} rows={3} value={f.details} onChange={set("details")} placeholder="Time, admission, host, anything dancers should know." data-testid="submit-details" /></div>
        </div>
        <DialogFooter>
          <button className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-semibold text-[#5C4A42] hover:border-[#C85A32]" onClick={() => onOpenChange(false)}>Cancel</button>
          <button className="inline-flex items-center gap-1.5 rounded-full bg-[#C85A32] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#B04B26] disabled:opacity-60" onClick={submit} disabled={busy} data-testid="submit-event-save">{busy ? "Sending…" : "Send event"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventCard({ e, featured = false }) {
  return (
    <Link to={`/events/${e.slug}`} data-testid={`event-card-${e.slug}`}
      className={`card-hover overflow-hidden rounded-2xl border bg-card ${featured ? "border-[#C85A32]/40 ring-1 ring-[#C85A32]/20" : "border-border"}`}>
      <div className="flex items-center justify-between gap-2 bg-[#2D1E18] px-5 py-3 text-sm font-bold text-[#FBEBE4]">
        <span className="inline-flex items-center gap-2"><CalendarDays size={16} /> {formatDateShort(e.date)}{e.start_time ? ` · ${formatTime(e.start_time)}` : ""}</span>
        <EventCategoryBadge category={e.category} />
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl font-bold text-[#2D1E18]">{e.title}</h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[#5C4A42]">{e.description}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6E5B51]">
          {e.venue && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {e.venue.name}</span>}
          {e.price ? <span className="inline-flex items-center gap-1 font-semibold text-[#B04B26]"><Ticket size={14} /> {formatPrice(e.price)}</span> : null}
        </div>
      </div>
    </Link>
  );
}

export default function Events() {
  const [scope, setScope] = useState("upcoming");
  const [cat, setCat] = useState("all");
  const [submitOpen, setSubmitOpen] = useState(false);
  const { data: events, isLoading } = useQuery({ queryKey: ["events", scope], queryFn: async () => (await api.get(`/events?scope=${scope}`)).data });
  const shown = (events || []).filter((e) => cat === "all" || (e.category || "club") === cat);
  const clubEvents = shown.filter((e) => e.category === "club");
  const otherEvents = shown.filter((e) => e.category !== "club");
  const showFeatured = cat === "all" && clubEvents.length > 0;

  return (
    <div>
      <PageHeader eyebrow="What's on" title="Club events &amp; socials"
        subtitle="Our own socials plus line dance events happening near us. Everyone welcome — bring your friends!" />
      <Container className="py-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-border bg-card p-1">
            {["upcoming", "past"].map((s) => (
              <button key={s} onClick={() => setScope(s)} data-testid={`events-tab-${s}`}
                className={`rounded-full px-5 py-2 text-sm font-semibold capitalize ${scope === s ? "bg-[#C85A32] text-white" : "text-[#5C4A42]"}`}>{s}</button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-border bg-card p-1">
            {CATS.map((c) => (
              <button key={c.key} onClick={() => setCat(c.key)} data-testid={`events-filter-${c.key}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${cat === c.key ? "bg-[#2D1E18] text-white" : "text-[#5C4A42]"}`}>{c.label}</button>
            ))}
          </div>
          <button onClick={() => setSubmitOpen(true)} data-testid="add-your-event-button"
            className="ml-auto inline-flex items-center gap-2 rounded-full border border-[#C85A32] px-4 py-2 text-sm font-bold text-[#B04B26] hover:bg-[#C85A32] hover:text-white"><Megaphone size={16} /> Add your event</button>
        </div>
        {isLoading ? <Spinner /> : !shown.length ? (
          <EmptyState title={`No ${scope} events`} subtitle="Check back soon — new events are added regularly." icon={CalendarDays} />
        ) : (
          <div className="space-y-10">
            {showFeatured && (
              <section data-testid="featured-club-events" className="rounded-3xl border border-[#C85A32]/30 bg-[#FBEBE4]/40 p-5 sm:p-6">
                <div className="mb-4 flex items-center gap-2">
                  <Star size={18} className="text-[#B04B26]" fill="currentColor" />
                  <h2 className="font-display text-xl font-extrabold text-[#2D1E18]">Our club events</h2>
                  <span className="rounded-full bg-[#C85A32] px-2.5 py-0.5 text-xs font-bold uppercase text-white">Featured</span>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {clubEvents.map((e) => <EventCard key={e.id} e={e} featured />)}
                </div>
              </section>
            )}
            {(showFeatured ? otherEvents : shown).length > 0 && (
              <section>
                {showFeatured && <h2 className="mb-4 font-display text-xl font-extrabold text-[#2D1E18]">Other socials near us</h2>}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {(showFeatured ? otherEvents : shown).map((e) => <EventCard key={e.id} e={e} />)}
                </div>
              </section>
            )}
          </div>
        )}
      </Container>
      <SubmitEventDialog open={submitOpen} onOpenChange={setSubmitOpen} />
    </div>
  );
}
